import React, {
  Component,
  RefObject,
  FocusEvent,
  KeyboardEvent,
  memo,
  ChangeEventHandler,
  FocusEventHandler
} from 'react'
import cn from 'classnames'
import withDeprecatedProp from '../../utils/hooks/depricatedPropsHoc'
import Icon from '../Icon2'
import Tooltip from '../Tooltip/Tooltip'
import Button from '../Button/Button'
import './TextInput.css'
import { debounce } from 'lodash'
import { defaultValue } from 'components/Table/util'

export type TextInputProps = {
  /**
  * Lets you set the width and height of input field
  */
  size?: 'default' | 'small' | string
  /**
   * Lets you set the width of the input field
   */
  width?: 'small' | 'medium' | 'large' | 'x-large' | 'full'
  /**
   * Makes a field read-only
   */
  isReadOnly?: boolean
  /**
   * Define the type of input element
   */
  type?: 'text' | 'password' | 'email' | 'number' | 'search' | 'url' | 'date' | 'time' | string
  /**
   * Define the name of the input element
   */
  name?: string
  /**
   * Define the ID of the input element
   */
  id?: string
  /**
   * Provide the classnames of input element wrapper
   */
  className?: string
  /**
   * Provide the classnames of input element
   */
  inputClassName?: string
  /**
   * Pass the callback function on a copy event
   */
  onCopy?: (value: string) => void
  /**
   * Pass the DOM reference to input element
   */
  inputRef?: RefObject<HTMLInputElement>
  /**
   * Defines whether input has an error or not
   */
  error?: boolean
  /**
   * Triggers a blur event on the click of ESC button
   */
  willBlurOnEsc?: boolean
  /**
   * Provides text suggestions while typing
   */
  autoComplete?: 'on' | 'off' | 'new-password'
  /**
   * Lets you autofocus input element
   */
  autoFocus?: boolean
  /**
   * Define value of input element
   */
  value?: any
  /**
   * Show eye suffix tooltip for input element on hover
   */
  suffix?: React.ReactNode
  /**
   * Show suffix visible always
   */
  suffixVisible?: boolean
  /**
   * Provides an option to switch between types: text and password
   */
  canShowPassword?: boolean
  /**
   * Pass the maximum length of characters to be allowed
   */
  maxLength?: number
  /**
   * Shows character length of input element value
   */
  showCharacterCount?: boolean
  /**
   * Pass an ID that you can use for testing purposes. It is applied as a data attribute (data-test-id).
   */
  testId?: string
  /**
   * Disables text input
   */
  disabled?: boolean
  /**
   * Define an input field as required
   */
  required?: boolean
  /**
   * Pass the callback function on a change event
   */
  onChange?: ChangeEventHandler<HTMLInputElement>
  /**
   * Pass the callback function on a blur event
   */
  onBlur?: FocusEventHandler<HTMLInputElement>
  /**
   * Pass the placeholder value
   */
  placeholder?: string
  /**
   * Pass the callback function on a keypress event
   */
  onKeyDown?: (value: any) => void
  /**
   * Provides the directionality of the text.
   */
  textDirection?: 'ltr' | 'rtl' | 'auto'
  /**
   * A new version of TextInput.
   */
  version?: 'v2'
  /**
   * Show heading icon on input element
   */
  prefix?: React.ReactNode
  /**
   * Provides an option to hide the character count error message.
   */
  hideCharCountError?: boolean
  /**
   * Provides an option to add Tooltip text for the view icon.
   */
  showTooltipText?: string
  /**
   * Provides an option to add Tooltip text for the hide icon.
   */
  hideTooltipText?: string
  debounce?: number
  /*** Provides an option to renders the label only without the input field.
   */
  onlyLabel?: boolean
  /**
   * Displays a copy icon in read-only mode.
   */
  showCopyIcon?: boolean
  /**
   * Content to copy to clipboard when the icon is clicked
   */
  copyContent?: string
  /**
   * copy label to show when the icon is hovered
   */
  copyLabel?: string
  /**
   * copy icon size
   */
  copyIconSize?: 'small' | 'medium' | 'large'
} & JSX.IntrinsicElements['input']

export type TextInputState = {
  value?: string
  visiblePassword?: boolean
}

interface RenderVisiblePasswordProps {
  togglePassworVisible: () => void
  visiblePassword: boolean
  type: 'text' | 'password' | 'email' | 'number' | 'search' | 'url' | 'date' | 'time' | string
  canShowPassword: boolean
  version: 'v1' | 'v2'
  showTooltipText?: string
  hideTooltipText?: string
}

export const RenderVisiblePassword = memo(
  ({ togglePassworVisible, visiblePassword, type, canShowPassword, version, showTooltipText, hideTooltipText }: RenderVisiblePasswordProps) => {
    if (type === 'password' && canShowPassword) {
      return (
        <span onClick={togglePassworVisible} className={`TextInput__suffix__wrapper ${version === 'v2' ? 'TextInput__suffix__wrapper-v2' : ''} TextInput__password`}>
          {!visiblePassword ? (
            <Tooltip content={showTooltipText || "View Password"} position="top">
              {version === 'v1' ? <Icon icon="Eye" /> : <Icon version='v2' size='medium' icon="Eye" />}
            </Tooltip>
          ) : (
            <Tooltip content={hideTooltipText || "Hide Password"} position="top">
              {version === 'v1' ? <Icon icon="EyeClose" /> : <Icon version='v2' size='medium' icon="EyeClose" />}
            </Tooltip>
          )}
        </span>
      )
    }
    return null
  }
)

const CharacterCount = ({ version, value, maxLength, showCharacterCount, hideCharCountError, testId }) => {
  if (!showCharacterCount || !maxLength) {
    return null
  }
  const length = value?.length || 0

  if (version === 'v2') {
    if (length <= maxLength) {
      return <span data-test-id={`${testId}-char-count`} className="TextInput__char-count">{`${length} / ${maxLength}`}</span>
    }

    if (!hideCharCountError) {
      return (
        <span data-test-id={`${testId}-max-char-reached`} className="TextInput__char-count TextInput__char-length--reached-text">
          Maximum character limit exceeded
        </span>
      )
    }
  } else {
    return (
      <span data-test-id='cs-text-input-char-count'
        className={`TextInput__char-length${length === maxLength ? ' TextInput__char-length--reached' : ''
          }`}>{`${length} / ${maxLength}`}</span>
    )
  }
  return null
}

export const RenderOnlyLabel = ({ copyIconSize,width, showCopyIcon, onlyLabel, value, copyContent, copyLabel, testId = "cs-text-clipboard" }) => {
  const [isCopied, setCopied] = React.useState(false);
  const text = copyContent || value;
  const handleCopy = (e) => {
    e.stopPropagation()
    e.preventDefault()
    const copyText = text
    navigator.clipboard.writeText(copyText).then(() => {
      setCopied(true)
    }).catch((err) => {
    });
  }

  const handleReset = () => {
    setCopied(false);
  };

  const isEnterPressed = (key: string): boolean => key === 'ENTER';

  if (onlyLabel) {
    return (
      <div className={`TextInput__only-label ${showCopyIcon?'TextInput__only-label-copy-option':''}`}>
        <div className={`TextInput__only-label-content TextInput--${width}`}> {value}</div>
        {showCopyIcon && (
          <div className='TextClipBoard' data-test-id="cs-text-clip-board" >
            <Tooltip content={!isCopied ? `Copy ${copyLabel}` : 'Copied'}
              position="top"
              trigger={isCopied ? 'mouseenter focusin' : 'mouseenter click focusin'}
              onHidden={handleReset} >
              <span tabIndex={0} aria-label={!isCopied ? `Copy ${copyLabel}` : 'Copied'} className='clipboard-copy' onClick={handleCopy} onKeyDown={(e: any) => {
                if (isEnterPressed(e.key)) {
                  e.preventDefault();
                  e.stopPropagation();
                  e.target.click();
                }
              }}>
                <Button version="v2" buttonType="tertiary" size={copyIconSize} className='TextClipBoard__icon' testId={`${testId}-copy-icon`} icon="v2-Copy" onlyIcon={true} onlyIconHoverColor="secondary" />
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    )
  }
  return null
}

export class TextInput extends Component<TextInputProps, TextInputState> {
  state = {
    value: this.props.value,
    visiblePassword: false
  }

  UNSAFE_componentWillReceiveProps(nextProps: TextInputProps) {
    if (this.props.value !== nextProps.value) {
      this.setState({
        value: nextProps.value
      })
    }
  }

  handleFocus = (e: FocusEvent) => {
    if (this.props.disabled) {
      ; (e.target as HTMLInputElement).select()
    }
  }

  handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const ESC = 'Escape'
    if (this.props.onKeyDown) {
      this.props.onKeyDown(e)
    }
    if (e.key === ESC && this.props.willBlurOnEsc) {
      e.currentTarget.blur()
    }
  }

  debounceOnChange = debounce((e) => this.props.onChange(e), this.props.debounce || 0)

  handleChange = (e) => {
    e.persist()
    if(this.props.debounce) {
      const value = e.target.value
      this.setState({ value })
      
      if (this.props.disabled || this.props.isReadOnly) return
      if (this.props.onChange && this.debounceOnChange) {
        this.debounceOnChange(e)
      }
    } else {
      const value = e.target.value
      this.setState({ value })
  
      if (this.props.disabled || this.props.isReadOnly) return
      if (this.props.onChange) {
        this.props.onChange(e)
      }
    }
  }

  renderSuffix = () => {
    if (this.props.suffix && !this.props.isReadOnly) {
      return <span className={this.props.suffixVisible ? 'TextInput__suffix__visible' : 'TextInput__suffix__wrapper'}>{this.props.suffix}</span>
    }
    return null
  }

  renderPrefix = () => {
    if (this.props.prefix) {
      return <span className="TextInput__prefix__wrapper">{this.props.prefix}</span>
    }
    return null
  }

  renderIsReadOnly = () => {
    if (this.props.isReadOnly) {
      return (
        <span className="TextInput__read-only__wrapper">
          {this.props.version === 'v2' ? <Icon withTooltip={true} tooltipContent={"Field locked"} tooltipPosition={"top"} icon="Lock" version='v2' size='medium' /> : <Icon icon="LockThin" />}
        </span>
      )
    }
    return null
  }

  togglePassworVisible = () => {
    this.setState({ visiblePassword: !this.state.visiblePassword })
  }

  handleClearSearch = (e: any) => {
    let { disabled, isReadOnly, onChange } = this.props
    if (disabled || isReadOnly) return
    let event: any = { target: { value: '' } }
    if (onChange) {
      onChange(event)
    }
    this.setState({ value: '' })
  }

  render() {

    const {
      className,
      inputClassName,
      placeholder = "Type something...",
      maxLength,
      disabled = false,
      required = false,
      isReadOnly = false,
      onChange,
      onBlur,
      onCopy,
      error,
      width = "full",
      value,
      type,
      name,
      testId = "cs-text-input",
      id,
      inputRef,
      willBlurOnEsc = true,
      autoComplete = "off",
      autoFocus = false,
      suffix,
      prefix,
      canShowPassword,
      showCharacterCount,
      textDirection = "auto",
      hideCharCountError = true,
      version,
      size,
      showTooltipText,
      hideTooltipText,
      ...otherProps
    } = this.props

    const widthClass = `TextInput--${width}`
    const versionClass = `${version === 'v2' ? 'TextInput__v2' : ''}`
    const classNames = cn(['TextInput'], className, [widthClass], {
      'TextInput--disabled': disabled,
      'TextInput--negative': error || (maxLength > 0 && this.state.value?.length > maxLength),
      'TextInput--with-count': showCharacterCount,
      'TextInput--suffix': !!suffix || type === 'password',
      TextInput__prefix: prefix,
      'TextInput__read-only': isReadOnly,
      'TextInput__read-only-disabled': isReadOnly && disabled,
      'TextInput--size-small-width': size === 'small' && width === 'full',
      'TextInput--size-small': size === 'small',
    })

    const suffixNode = this.renderSuffix()
    const prefixNode = this.renderPrefix()
    const isReadOnlyNode = this.renderIsReadOnly()

    let inputValueProps:any = {
      value:this.state.value 
    }

    if(this.props.debounce) {
      inputValueProps = {
        defaultValue: this.state.value 
      }
    }

    return (
      <> {this.props.onlyLabel
        ? <RenderOnlyLabel copyIconSize={this.props.copyIconSize} onlyLabel={this.props.onlyLabel} showCopyIcon={this.props.showCopyIcon} value={this.props.value} width={this.props.width} copyLabel={this.props.copyLabel} copyContent={this.props.copyContent} />
        : <div data-test-id={testId} className={classNames}>
          {prefixNode}
          {suffixNode}
          {isReadOnlyNode}
          <RenderVisiblePassword
            canShowPassword={canShowPassword}
            type={type}
            visiblePassword={this.state.visiblePassword}
            togglePassworVisible={this.togglePassworVisible}
            version={version}
            showTooltipText={showTooltipText}
            hideTooltipText={hideTooltipText}
          />
          <input
            onKeyDown={this.handleKeyDown}
            aria-label={name}
            className={`TextInput__input${inputClassName ? ` ${inputClassName}` : ''}${versionClass ? ` ${versionClass}` : ''
              }`}
            id={id}
            name={name}
            required={required}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            onBlur={onBlur}
            onFocus={this.handleFocus}
            dir={textDirection}
            onChange={this.handleChange}
            onWheel={(e) => e.currentTarget.blur()}
            value={this.state.value}
            type={!this.state.visiblePassword ? type : 'text'}
            ref={inputRef}
            {...otherProps}
          />
          {type == 'search' && (
            <div
              data-test-id="textInput-search-clear"
              onClick={this.handleClearSearch}
              className="TextInput__cancel-icon">
              {this.state.value && (
                <Tooltip content="Cancel Search" position="top">
                  <Icon icon="Cancel" />
                </Tooltip>
              )}
            </div>)
          }
          <CharacterCount
          version={version}
          value={this.state.value}
          maxLength={maxLength}
          showCharacterCount={showCharacterCount}
          hideCharCountError={hideCharCountError}
          testId={testId}
        />
        </div>
      }
      </>
    )
  }
}

export default withDeprecatedProp(TextInput, {
  showCharCount: 'showCharacterCount',
  toggleVisiblePassword: 'canShowPassword'
})